import { useEffect, useEffectEvent, useState } from 'react'

import { useNavigate, useSearch } from '@tanstack/react-router'

import { usePostHog } from '@posthog/react'
import { useDebounce } from '@uidotdev/usehooks'

/**
 * Options for useTableSearch hook
 */
export type UseTableSearchOptions = {
	/** URL param name for search query (default: 'q') */
	paramName?: string
	/** Debounce delay in ms (default: 300) */
	debounceMs?: number
	/** PostHog analytics event name */
	analyticsEvent?: string
}

/**
 * Return type for useTableSearch hook
 */
export type UseTableSearchReturn = {
	/** Current search query value */
	query: string
	/** Update search query */
	setQuery: (query: string) => void
	/** Debounced query value (for API calls) */
	debouncedQuery: string
}

/**
 * Hook for managing table search state with URL sync
 *
 * Features:
 * - Syncs search query with URL params
 * - Debounces input to prevent excessive URL updates
 * - Optional PostHog analytics tracking
 * - Reads initial value from URL on mount
 *
 * @example
 * const { query, setQuery, debouncedQuery } = useTableSearch({
 *   analyticsEvent: 'playbooks_search',
 * })
 *
 * // Pass debouncedQuery to useDataTable for filtering
 * const { columns, options } = useDataTable({
 *   globalFilter: debouncedQuery,
 *   ...
 * })
 */
export function useTableSearch(
	options: UseTableSearchOptions = {},
): UseTableSearchReturn {
	const { paramName = 'q', debounceMs = 300, analyticsEvent } = options

	const navigate = useNavigate()
	const posthog = usePostHog()

	// Get current search params (strict: false allows use in any route)
	const searchParams = useSearch({ strict: false }) as Record<
		string,
		string | undefined
	>
	const urlQuery = searchParams[paramName] ?? ''

	// Local state for immediate UI updates
	const [query, setQuery] = useState(urlQuery)

	// Track previous URL value for sync
	const [prevUrlQuery, setPrevUrlQuery] = useState(urlQuery)

	// Sync from URL -> local state when URL changes externally
	if (urlQuery !== prevUrlQuery) {
		setPrevUrlQuery(urlQuery)
		setQuery(urlQuery)
	}

	// Debounced value for URL updates and API calls
	const debouncedQuery = useDebounce(query, debounceMs)

	// Stable function for URL update (avoids useEffect dependencies)
	const updateURL = useEffectEvent(() => {
		navigate({
			to: '.',
			search: (prev) => ({
				...prev,
				[paramName]: debouncedQuery || undefined,
			}),
		})
	})

	// Sync from local state -> URL when debounced value changes
	useEffect(() => {
		// Track search analytics (only for non-empty searches)
		if (analyticsEvent && debouncedQuery !== '') {
			posthog.capture(analyticsEvent, {
				query: debouncedQuery,
			})
		}

		updateURL()
	}, [debouncedQuery, analyticsEvent, posthog])

	return {
		query,
		setQuery,
		debouncedQuery,
	}
}
